import {
  ACCESSIBILITY_URL,
  CUSTOMER_SUPPORT_URL,
  HEALTH_INFORMATION_EXCHANGE_URL,
  LAYOUT_CONSTANTS,
  NO_SURPRISES_ACT_URL,
  NON_DISCRIMINATION_NOTICE_URL,
  NOTICE_OF_AVAILABILITY_URL,
  NOTICE_OF_PRIVACY_PRACTICES_URL,
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
} from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Footer as AbyssFooter } from "@uhg-abyss/web/ui/Footer";
import { Text } from "@uhg-abyss/web/ui/Text";

const AdditionalResourcesLink = styled("a", {
  color: "$app.color.text.footer.rest",
  textDecoration: "underline",
  fontWeight: "400",
  flexWrap: "wrap",
  fontSize: "14.59px",
  display: "inline",
  "&:hover": {
    color: "$app.color.text.footer.hover",
  },
  "&:active": {
    color: "$app.color.text.footer.active",
  },
  variants: {
    isTablet: {
      true: { pointerEvents: "auto" },
      false: { pointerEvents: "inherit" },
    },
  },
});

const MOBILE_FONT_SIZE_VARIANT = {
  isMobile: {
    true: { fontSize: "13px" },
    false: { fontSize: "14.59px" },
  },
} as const;

const FooterSeparator = styled("span", {
  color: "$app.color.text.footer.rest",
  display: "inline-block",
  verticalAlign: "baseline",
  fontWeight: "400",
  variants: MOBILE_FONT_SIZE_VARIANT,
});

const EmergencyMessageText = styled(Text, {
  fontSize: "14.7px",
  display: "inline",
});

const EmergencyLi = styled("li", {
  display: "inline",
  flexWrap: "wrap",
});

const SubFooterLi = styled("li", {
  marginTop: "16px",
  alignSelf: "stretch",
  justifyContent: "flex-start",
  display: "inline-flex",
  variants: {
    isMobile: {
      true: {
        alignItems: "unset",
        gap: "20px",
        flexDirection: "column",
      },
      false: {
        alignItems: "center",
        gap: "0",
        flexDirection: "row",
      },
    },
  },
});

const CopyrightHolder = styled("div", {
  paddingRight: "31.42px",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  display: "inline-flex",
  maxWidth: "100%",
});

const CopyrightText = styled(Text, {
  justifyContent: "center",
  display: "flex",
  flexDirection: "column",
  whiteSpace: "normal",
  wordWrap: "break-word",
  variants: MOBILE_FONT_SIZE_VARIANT,
});

const LinksList = styled("ul", {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px 16px",
  alignItems: "flex-start",
  listStyle: "none",
  padding: 0,
  margin: 0,
  variants: {
    isMobile: {
      true: { flexDirection: "column" },
      false: { flexDirection: "row" },
    },
  },
});

const LinkItem = styled("div", {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
});

const SubFooterLinkText = styled(AbyssFooter.Link, {
  fontWeight: "400",
  variants: {
    ...MOBILE_FONT_SIZE_VARIANT,
    isTablet: {
      true: { "&:hover": { textDecoration: "underline" } },
      false: { "&:hover": { textDecoration: "none" } },
    },
  },
});

export const Footer = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const isTablet = useMediaQuery(MEDIA_QUERIES.tabletMax);

  const subFooterLinks = [
    { label: "Privacy Policy", href: PRIVACY_POLICY_URL },
    { label: "Terms Of Use", href: TERMS_OF_USE_URL },
    { label: "Accessibility", href: ACCESSIBILITY_URL },
    {
      label: "Notice of Privacy Practices",
      href: NOTICE_OF_PRIVACY_PRACTICES_URL,
    },
    { label: "No Surprises Act", href: NO_SURPRISES_ACT_URL },
    { label: "Non Discrimination Notice", href: NON_DISCRIMINATION_NOTICE_URL },
    { label: "Notice of Availability", href: NOTICE_OF_AVAILABILITY_URL },
    {
      label: "Health Information Exchange",
      href: HEALTH_INFORMATION_EXCHANGE_URL,
    },
  ];

  return (
    <AbyssFooter
      variant="alt"
      css={{
        "abyss-footer-container": {
          backgroundColor: "$app.color.default.background",
          borderTopColor: "$app.color.default.border",
          borderTopWidth: "1px",
          alignItems: "center",
          margin: "auto",
          maxWidth: LAYOUT_CONSTANTS.maxPageWidth,
          width: "100%",
          padding: isMobile ? "32px 0 155px" : "80px 0 68px",
          "& .abyss-footer-section": {
            gap: "0 !important",
          },
          "& .abyss-footer-section .abyss-footer-list-container": {
            maxWidth: LAYOUT_CONSTANTS.maxContentWidth,
            gap: "16px !important",
            padding: isMobile ? "0 20px" : "0 32px",
          },
          "& .abyss-link-icon": {
            display: "none",
          },
        },
        "abyss-footer-copyright": {
          display: "none",
        },
      }}
    >
      <AbyssFooter.Section>
        <EmergencyLi>
          <EmergencyMessageText fontWeight="regular" color="$app.color.black">
            {
              "If you are in need of emergency emotional assistance, call or text the 988 Suicide and Crisis Lifeline at 988 or use Lifeline Chat for 24/7 free and confidential support. "
            }
          </EmergencyMessageText>
          <AdditionalResourcesLink
            isTablet={isTablet}
            href={`${CUSTOMER_SUPPORT_URL}`}
            target="_blank"
            rel="noopener noreferrer"
            id="obc-footer-additional-resources-link"
            data-obc-label="Footer Additional Resources Link"
            onMouseEnter={(e) => {
              if (!isTablet) {
                (e.target as HTMLAnchorElement).style.textDecoration = "none";
              }
            }}
            onMouseLeave={(e) => {
              if (!isTablet) {
                (e.target as HTMLAnchorElement).style.textDecoration = "underline";
              }
            }}
          >
            Click here for additional resources
          </AdditionalResourcesLink>
        </EmergencyLi>
        <SubFooterLi isMobile={isMobile}>
          <CopyrightHolder>
            <CopyrightText isMobile={isMobile}>
              © 2025 Optum. All rights reserved. Stock photos used.
            </CopyrightText>
          </CopyrightHolder>
          <LinksList isMobile={isMobile}>
            {subFooterLinks.map((link) => {
              const footerLinkId = `obc-footer-subfooter-link-${link.label.replaceAll(/\s+/g, "-").toLowerCase()}`;

              return (
                <LinkItem key={link.label}>
                  <FooterSeparator aria-hidden="true" isMobile={isMobile}>
                    |
                  </FooterSeparator>
                  <SubFooterLinkText
                    isMobile={isMobile}
                    isTablet={isTablet}
                    id={footerLinkId}
                    data-obc-label={`Footer Subfooter ${link.label} Link`}
                    href={link.href}
                  >
                    {link.label}
                  </SubFooterLinkText>
                </LinkItem>
              );
            })}
          </LinksList>
        </SubFooterLi>
      </AbyssFooter.Section>
    </AbyssFooter>
  );
};
